import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { debounceTime } from 'rxjs';
import { LayoutService, layoutConfig } from './layout.service';
import { AuthService } from '../../../components/auth/data-access/auth.service';

/**
 * Persists the user's dashboard theme (dark/light, primary, surface, preset,
 * menu mode) so it survives reloads and follows them across devices on login.
 *
 * Hybrid strategy:
 *  - localStorage cache  → applied instantly on boot so there's no flash of the
 *    default theme, and is the freshest value for THIS device on a reload.
 *  - backend (per user)  → source of truth across devices; read on a fresh
 *    login (see applyFromServer) and written on every change.
 */
@Injectable({ providedIn: 'root' })
export class ThemePreferenceService {
  private layout = inject(LayoutService);
  private auth = inject(AuthService);
  private http = inject(HttpClient);

  private readonly KEY = 'clubside.uiConfig';
  private readonly FIELDS: (keyof layoutConfig)[] = [
    'darkTheme',
    'preset',
    'primary',
    'surface',
    'menuMode',
    'menuTheme',
    'inputStyle',
    'ripple',
  ];

  // Serialized form of the last value we applied and persisted. Guards against
  // a set() → configUpdate → persist echo looping back to the server.
  private lastApplied = '';
  private watching = false;

  private pick(cfg: layoutConfig): Record<string, any> {
    const out: Record<string, any> = {};
    for (const key of this.FIELDS) {
      const value = cfg[key];
      if (value !== undefined && value !== null) out[key] = value;
    }
    return out;
  }

  /** Apply the device-cached theme instantly on boot (safe pre-login). */
  applyCache(): void {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(this.KEY);
    } catch {
      return;
    }
    if (!raw) return;
    try {
      this.apply(JSON.parse(raw), false);
    } catch {
      /* ignore a corrupt cache */
    }
  }

  /** The server is the source of truth. Call this right after a fresh login. */
  applyFromServer(uiConfig?: Record<string, any> | null): void {
    if (uiConfig && Object.keys(uiConfig).length) this.apply(uiConfig, true);
  }

  /** Persist every change (debounced): localStorage always, backend when signed in. */
  startWatching(): void {
    if (this.watching) return;
    this.watching = true;
    this.layout.configUpdate$.pipe(debounceTime(500)).subscribe((cfg) => {
      const picked = this.pick(cfg);
      const serialized = JSON.stringify(picked);
      if (serialized === this.lastApplied) return; // no real change / echo of apply()
      this.lastApplied = serialized;
      try {
        localStorage.setItem(this.KEY, serialized);
      } catch {
        /* storage full or disabled; the cache is best-effort */
      }
      if (this.auth.isAuthenticatedComputedSignal()) {
        this.http
          .patch('/users/me/preferences', { uiConfig: picked })
          .subscribe({ error: () => {} });
      }
    });
  }

  private apply(uiConfig: Record<string, any>, cache: boolean): void {
    const merged = { ...this.layout.layoutConfig(), ...uiConfig } as layoutConfig;
    // Pre-seed the dedupe key so the resulting configUpdate emission doesn't
    // bounce straight back to the server.
    this.lastApplied = JSON.stringify(this.pick(merged));
    this.layout.layoutConfig.set(merged);
    this.layout.toggleDarkMode(merged);
    if (cache) {
      try {
        localStorage.setItem(this.KEY, this.lastApplied);
      } catch {
        /* best-effort */
      }
    }
  }
}
